import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { useQuery } from '../QueryContext';
import './Home.css';
import slide1 from '../assets/fondo-home4.avif';
import { Calendar, Database, ChartColumn, Archive, Users } from 'lucide-react';
import { setCachedOptions, getCachedOptions } from '../utils/dashboardCache';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const ChevronIcon = ({ isOpen }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" height="24" viewBox="0 0 24 24" 
    fill="none" stroke="currentColor" strokeWidth="2" 
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
  >
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

export default function Home() {
  const { session, hasAccess } = useAuth(); // Añadir hasAccess
  const navigate = useNavigate();
  
  const { getSalesFilters, loading: apiLoading } = useQuery();

  const [isSheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const prewarmFilters = async () => {
      if (getCachedOptions()) {
        console.log('Filtros ya existentes en caché.');
        return;
      }

      try {
        console.log('Solicitando pre-carga de filtros al servidor...');
        const config = await getSalesFilters();
        
        if (config && config.availableOptions) {
          setCachedOptions(config.availableOptions);
          console.log('Caché de filtros actualizado desde el servidor.');
        }
      } catch (e) {
        console.error("Error en pre-carga de filtros:", e);
      }
    };

    prewarmFilters();
  }, [getSalesFilters]);

  // Definir todos los botones del menú rápido con sus permisos
  const quickMenuItems = [
    { path: '/dprincipal', label: 'Daily', icon: <ChartColumn size={40} />, hasAccess: hasAccess('/dprincipal') },
    { path: '/inventario', label: 'Inventario', icon: <Archive size={40} />, hasAccess: hasAccess('/inventario') },
    { path: '/asesores', label: 'Asesores', icon: <Users size={40} />, hasAccess: hasAccess('/asesores') },
    { path: '/bultos', label: 'Bultos', icon: <Archive size={40} />, hasAccess: hasAccess('/bultos') },
    { path: '/usdsales', label: 'Ventas USD', icon: <ChartColumn size={40} />, hasAccess: hasAccess('/usdsales') },
    { path: '/activacion', label: 'Activación', icon: <Users size={40} />, hasAccess: hasAccess('/activacion') },
    { path: '/sugeridos', label: 'Sugeridos', icon: <ChartColumn size={40} />, hasAccess: hasAccess('/sugeridos') },
    { path: '/sellout', label: 'SellOut', icon: <ChartColumn size={40} />, hasAccess: hasAccess('/sellout') },
    { path: '/pedidos', label: 'Pedidos', icon: <Archive size={40} />, hasAccess: hasAccess('/pedidos') },
    { path: '/facturación', label: 'Facturación', icon: <Archive size={40} />, hasAccess: hasAccess('/facturación') },
    { path: '/tradeform', label: 'Trade Form', icon: <Users size={40} />, hasAccess: hasAccess('/tradeform') },
    { path: '/report', label: 'Reporte', icon: <Calendar size={40} />, hasAccess: hasAccess('/report') },
  ];

  // Filtrar solo los que tienen acceso
  const visibleQuickMenuItems = quickMenuItems.filter(item => item.hasAccess);

  // Verificar si el botón de Reporte debe mostrarse
  const canSeeReport = hasAccess('/report');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={isMobile ? { duration: 0.3 } : { duration: 0.6 }}>
      <motion.div className="home-container" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
        <div className="hero-welcome">
          <div className="hero-overlay" />
          <img src={slide1} alt="Fondo Bienvenida" />
          <div className="hero-welcome-content">
            <h1>Bienvenido,<br /><span>{session?.user?.nombre || 'Usuario'}</span></h1>
            <div className="hero-divider" />
            <h3>{session?.user?.cargo || 'Consultor Comercial'}</h3>
          </div>
        </div>

        <div className="stylish-divider"/>
      </motion.div>

      <div className="floating-bookmarks">
        {/* Botón de Reporte - solo si tiene acceso */}
        {canSeeReport && (
          <button 
            className="bookmark-btn agenda" 
            onClick={() => navigate('/report')}
            title="Ir a Reporte"
          >
            <Calendar size={18}/>  Reporte
          </button>
        )}
        
        <div className={`db-status-indicator ${apiLoading ? 'loading' : 'ready'}`} 
             title={apiLoading ? 'Conectando con el servidor...' : 'Servidor Conectado'}>
          <Database size={16} />
          <div className="status-dot"></div>
        </div>
      </div> 

      {/* Bottom Sheet - solo mostrar si hay items visibles */}
      {visibleQuickMenuItems.length > 0 && (
        <div className={`bottom-sheet ${isSheetOpen ? 'open' : ''}`}>
          <button onClick={() => setSheetOpen(s => !s)} className="sheet-handle">
            <ChevronIcon isOpen={isSheetOpen} />
          </button>
          <div className="sheet-content">
            <h2>Menú Rápido</h2>
            <div className="quick-menu-carousel" style={{ 
              display: 'flex', 
              gap: '15px', 
              overflowX: 'auto', 
              padding: '15px 5px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}>
              {visibleQuickMenuItems.map((item, index) => (
                <motion.div 
                  key={item.path}
                  className="quick-menu-card" 
                  onClick={() => navigate(item.path)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="home-buttons-icon">
                    {item.icon}
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>
                    {item.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}